/**
 * Route contracts and path matching shared by the customer, retailer and admin
 * surfaces.
 *
 * This lives in its own package because all three apps need it. It previously
 * sat inside the customer app, which meant the retailer portal and admin console
 * imported from the customer bundle purely to borrow a type — a dependency
 * pointing the wrong way, and one that would have made it impossible to build or
 * deploy a portal without the storefront.
 *
 * No router library is involved. These surfaces need path matching and nothing
 * else, and keeping it dependency-free keeps the customer bundle small on the
 * mobile connections most of our shoppers are on.
 */

/** Fields every surface's route table shares. */
export interface AppRouteContract {
  path: string;
  /** Human-readable screen name, used in navigation and tests. */
  screen: string;
  /** Required permission, or null for a public screen. */
  permission: string | null;
  /** Feature flag guarding the screen, or null when always available. */
  flag?: string | null;
  /** True when the screen must not be reached without a satisfied MFA challenge. */
  requiresMfa?: boolean;
}

export interface RouteMatch<TRoute extends AppRouteContract> {
  route: TRoute;
  params: Readonly<Record<string, string>>;
}

/**
 * Trims query and hash, collapses duplicate slashes and drops a trailing slash
 * so that `/stores/` and `/stores` are the same screen.
 */
export function normalizePath(pathname: string): string {
  const withoutQuery = pathname.split('?')[0]!.split('#')[0]!;
  const collapsed = `/${withoutQuery}`.replace(/\/+/g, '/');
  return collapsed.length > 1 ? collapsed.replace(/\/$/, '') : '/';
}

function parameterCount(pattern: string): number {
  return pattern.split('/').filter((segment) => segment.startsWith(':')).length;
}

function matchPath(pattern: string, path: string): Record<string, string> | null {
  const patternParts = pattern.split('/').filter(Boolean);
  const pathParts = path.split('/').filter(Boolean);
  if (patternParts.length !== pathParts.length) return null;
  const params: Record<string, string> = {};
  for (let index = 0; index < patternParts.length; index += 1) {
    const expected = patternParts[index]!;
    const actual = pathParts[index]!;
    if (expected.startsWith(':')) {
      // An empty parameter is not a match; `/products/` must not resolve to a
      // product page with a blank slug.
      if (actual.length === 0) return null;
      params[expected.slice(1)] = decodeURIComponent(actual);
      continue;
    }
    if (expected !== actual) return null;
  }
  return params;
}

/**
 * Matches a pathname against a route table.
 *
 * Routes with fewer parameters are tried first, so a concrete screen such as
 * `/account/lists` can never be swallowed by a pattern like `/account/:section`.
 * Returns null for an unknown path so the caller renders a real not-found screen
 * rather than silently falling back to the home page.
 */
export function resolveRouteIn<TRoute extends AppRouteContract>(
  routes: readonly TRoute[],
  pathname: string,
): RouteMatch<TRoute> | null {
  const path = normalizePath(pathname);
  const candidates = [...routes].sort((a, b) => parameterCount(a.path) - parameterCount(b.path));
  for (const route of candidates) {
    const params = matchPath(route.path, path);
    if (params !== null) return { route, params };
  }
  return null;
}

/** Builds a concrete href from a route pattern, encoding each parameter value. */
export function buildPath(pattern: string, params: Readonly<Record<string, string>> = {}): string {
  return normalizePath(
    pattern
      .split('/')
      .map((segment) => {
        if (!segment.startsWith(':')) return segment;
        const value = params[segment.slice(1)];
        if (value === undefined) throw new Error(`Missing route parameter "${segment.slice(1)}".`);
        return encodeURIComponent(value);
      })
      .join('/'),
  );
}

/** Returns duplicate paths in a route table, for startup and test assertions. */
export function duplicatePaths(routes: readonly AppRouteContract[]): readonly string[] {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const route of routes) {
    if (seen.has(route.path)) duplicates.add(route.path);
    seen.add(route.path);
  }
  return [...duplicates].sort();
}
